import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapGrid } from "@/components/MapViewer";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Layers, X, Download, Loader2 } from "lucide-react";

const chunk = (arr, n) =>
  arr.reduce((a, _, i) => (i % n ? a : [...a, arr.slice(i, i + n)]), []);

// Full 18-map standard suite viewer. Loads the manifest (pre-rendered stored maps come
// back instantly) then lazily renders the remaining plates in small batches.
export default function MapSuiteDialog({ open, onOpenChange, projectId, areaId, areaName }) {
  const [items, setItems] = useState([]);
  const [maps, setMaps] = useState({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [mode, setMode] = useState("18");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setMaps({});
      setItems([]);
      setProgress({ done: 0, total: 0 });
      try {
        const { data } = await api.get(`/projects/${projectId}/areas/${areaId}/map-suite?mode=${mode}`);
        if (cancelled) return;
        const suite = data.suite || [];
        setItems(suite.map((s) => ({ key: s.key, label: s.label })));
        const seed = {};
        suite.forEach((s) => { if (s.uri) seed[s.key] = s.uri; });
        setMaps(seed);
        const pending = suite.filter((s) => !s.uri);
        setProgress({ done: suite.length - pending.length, total: suite.length });
        for (const batch of chunk(pending, 3)) {
          if (cancelled) return;
          // eslint-disable-next-line no-await-in-loop
          await Promise.all(
            batch.map(async (s) => {
              try {
                const r = await api.get(
                  `/projects/${projectId}/areas/${areaId}/map/${s.key}?inline=1`
                );
                if (!cancelled) setMaps((m) => ({ ...m, [s.key]: r.data.uri }));
              } catch {
                /* tile stays "Not available" */
              } finally {
                if (!cancelled) setProgress((p) => ({ ...p, done: p.done + 1 }));
              }
            })
          );
        }
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId, areaId, mode]);

  const [zipping, setZipping] = useState(false);

  const downloadSuiteZip = async () => {
    setZipping(true);
    const tid = toast.loading("Preparing HD map-suite ZIP…");
    try {
      const r = await api.get(`/projects/${projectId}/areas/${areaId}/suite-zip`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SYMIN_${areaId}_Map_Suite.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Map-suite ZIP downloaded (all 18 plates)", { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    } finally {
      setZipping(false);
    }
  };

  const downloadHd = async (key) => {
    try {
      const r = await api.get(`/projects/${projectId}/areas/${areaId}/map/${key}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SYMIN_${areaId}_${key}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[94vw] w-[94vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden [&>button]:hidden"
        data-testid="map-suite-dialog"
      >
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 bg-white shrink-0">
          <DialogTitle
            className="text-sm font-bold text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            <Layers size={16} className="text-orange-500" />
            {mode === "04" ? "Quick 4-Map Set" : "Standard 18-Map Suite"} - {areaName || areaId}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Full standard 18-map suite for this licence area; each plate is strictly clipped to the boundary and downloadable in high resolution.
          </DialogDescription>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs font-semibold" data-testid="suite-mode-toggle">
              {["04", "18"].map((m) => (
                <button key={m} data-testid={`suite-mode-${m}`} onClick={() => setMode(m)}
                  className={`px-3 h-8 transition-colors ${mode === m ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>
                  {m === "04" ? "04 Maps" : "18 Maps"}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={downloadSuiteZip} disabled={zipping}
              data-testid="suite-download-zip" className="h-8 border-slate-300 text-xs">
              {zipping ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Download size={13} className="mr-1.5" />}
              Download ZIP (all 18)
            </Button>
            <span className="text-xs text-slate-500 tabular-nums" data-testid="suite-progress">
              {progress.total ? `${progress.done}/${progress.total} maps` : "Loading…"}
            </span>
            <button
              data-testid="suite-close"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 grid place-items-center rounded-md text-slate-600 hover:bg-slate-100"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-slate-50">
          <MapGrid items={items} maps={maps} onHd={downloadHd} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
